/**
 * AnalyticsRepository — the Analytics (beta) tool's one data source: the getAnalytics
 * callable (functions/analytics/getAnalytics.js), which computes every figure under one
 * set of filters server-side. Nothing here reads Firestore directly.
 */
import { httpsCallable } from 'firebase/functions';

export class AnalyticsRepository {
  constructor(functions) {
    // Long periods stream tens of thousands of sessions; give them room.
    this.call = httpsCallable(functions, 'getAnalytics', { timeout: 180000 });
    this.adImages = httpsCallable(functions, 'getCampaignAdImagesCallable');
    this.adImage = httpsCallable(functions, 'setCampaignAdImageCallable');
    this.adStarts = httpsCallable(functions, 'getAdStartsCallable', { timeout: 120000 });
    this.campaignLabel = httpsCallable(functions, 'setCampaignLabelCallable');
  }

  /** Every stored ad picture and ad text: { images: { key: dataUrl }, texts: { key: text } }. */
  async getAdImages() {
    const res = await this.adImages({});
    const d = res.data || {};
    return { images: d.images || {}, texts: d.texts || {} };
  }

  /**
   * Store an ad's picture (a small data URL) and/or its text. A field left out is kept; an
   * empty string removes it. Admins only.
   * @param {{image?: string, text?: string}} details
   */
  async setAdDetails(key, details) {
    const res = await this.adImage({ key, ...details });
    return res.data;
  }

  /**
   * How each Meta ad started: { [campaignKey]: { firstHitDay, launchDay, day1, day2, week,
   * complete, hoursSoFar } } — clicks by people in the first 24 h / 48 h / 7 days from the
   * ad's own launch (functions/analytics/adStarts.js).
   */
  async getAdStarts(keys) {
    const res = await this.adStarts({ keys });
    return (res.data && res.data.starts) || {};
  }

  /**
   * Name a campaign and/or store its Ad Library id. A field left out is kept. Admins only.
   * @param {{key: string, label?: string, adLibraryId?: string}} payload
   */
  async setCampaignLabel(payload) {
    const res = await this.campaignLabel(payload);
    return res.data;
  }

  /** @param params { range, from, to, environment, peopleOnly, includeInternal, sections, shift } */
  async get(params) {
    const res = await this.call(params);
    return res.data;
  }
}

export default AnalyticsRepository;
